import { useNavigate } from 'react-router-dom';
import { CalendarDays, CalendarClock, CheckSquare } from 'lucide-react';
import { format, parseISO, isToday, isTomorrow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Card } from '../ui/Card';
import { WIDGET_HEADER_FLUSH, WidgetCardHeader } from './WidgetCardHeader';
import { Spinner } from '../ui/Spinner';
import { useCalendarEvents } from '../../hooks/queries/useCalendarEvents';
import type {
  CalendarEvent,
  CalendarEventKind,
  CalendarFilters,
} from '../../services/calendar/calendarAggregate';
import {
  groupEventsByDay,
  revealSlice,
  selectUpcomingEvents,
} from '../../services/dashboard/ordreDuJour';
import { WidgetInfiniteList } from './WidgetInfiniteList';
import { useTaskModalStore } from '../../store/taskModalStore';

const NO_FILTERS: CalendarFilters = { lawyerIds: [], dossierId: null, kinds: [] };

function iconFor(kind: CalendarEventKind) {
  if (kind === 'hearing') {
    return <CalendarClock className="w-3.5 h-3.5 text-orange-600 flex-shrink-0" aria-hidden />;
  }
  if (kind === 'task') {
    return <CheckSquare className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" aria-hidden />;
  }
  if (kind === 'appointment') {
    return <CalendarClock className="w-3.5 h-3.5 text-[#c9a84c] flex-shrink-0" aria-hidden />;
  }
  return null;
}

function dayLabel(dateStr: string): string {
  const d = parseISO(dateStr);
  if (isToday(d)) return "Aujourd'hui";
  if (isTomorrow(d)) return 'Demain';
  return format(d, 'EEEE d MMMM', { locale: fr });
}

function timeLabel(item: CalendarEvent): string | null {
  if (item.allDay) return null;
  return format(parseISO(item.start), 'HH:mm', { locale: fr });
}

export function AgendaWidget() {
  const navigate = useNavigate();
  const openTask = useTaskModalStore((s) => s.openTask);
  const { events, isLoading, isError } = useCalendarEvents(NO_FILTERS);

  // Tout ce qui est prévu à partir d'aujourd'hui : le défilement infini montre les semaines suivantes.
  const windowed = selectUpcomingEvents(events, new Date());

  const handleEventClick = (item: CalendarEvent) => {
    if (item.kind === 'task') {
      openTask(item.id.replace(/^task:/, ''));
    } else {
      navigate(item.href);
    }
  };

  return (
    <Card
      className="h-full"
      headerClassName={WIDGET_HEADER_FLUSH}
      header={<WidgetCardHeader icon={CalendarDays} title="Agenda" href="/calendrier" />}
    >
      {isError ? (
        <p className="text-sm text-neutral-400 text-center py-3">Agenda indisponible</p>
      ) : isLoading ? (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      ) : windowed.length === 0 ? (
        <p className="text-sm text-neutral-400 text-center py-3">
          Rien de prévu
        </p>
      ) : (
        <WidgetInfiniteList itemCount={windowed.length} className="space-y-3">
          {(visibleCount) => {
            const days = groupEventsByDay(revealSlice(windowed, visibleCount).items);
            return days.map(([day, items]) => (
              <div key={day}>
                <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-1">
                  {dayLabel(day)}
                </p>
                <ul className="space-y-0.5">
                  {items.map((item) => {
                    const time = timeLabel(item);
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => handleEventClick(item)}
                          className="w-full flex items-center gap-2 text-left py-1 hover:bg-neutral-50 rounded px-1 transition-colors"
                        >
                          {iconFor(item.kind)}
                          <span className="text-sm text-neutral-700 flex-1 truncate">
                            {item.title}
                          </span>
                          {time && (
                            <span className="text-xs text-neutral-500 flex-shrink-0 tabular-nums">
                              {time}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ));
          }}
        </WidgetInfiniteList>
      )}
    </Card>
  );
}
